import { inject, Injectable } from '@angular/core';
import { ActivationEnd, NavigationEnd, type Params, Router } from '@angular/router';
import { filter, map, shareReplay, withLatestFrom } from 'rxjs/operators';
import { CoreModalService } from './core-modal.service';
import { WebModalService } from './web-modal.service';

@Injectable({
  providedIn: 'root',
})
export class WebModalListenerService {
  private router = inject(Router);
  private webModalService = inject(WebModalService);
  private coreModalService = inject(CoreModalService);

  initQueryParamsListener(): void {
    this.listenForQueryParams();
  }

  private listenForQueryParams() {
    // ActivationEnd has the router snapshot with the query parameters
    // this fires multiple times
    const activationEnd$ = this.router.events.pipe(
      filter((e): e is ActivationEnd => e instanceof ActivationEnd),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // NavigationEnd is used to ensure we only handle the query parameters after the navigation has completed
    // this fires only once
    const navigationEnd$ = this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd));

    navigationEnd$
      .pipe(
        withLatestFrom(activationEnd$),
        map(([, act]) => act.snapshot.queryParams),
      )
      .subscribe((queryParams) => this.reconcileModals(queryParams));
  }

  /**
   * Makes the open modals match the address bar. This runs after every navigation and is idempotent:
   * opening a modal from inside the application writes its own query parameters, after which this finds
   * the modals and the address bar already in sync, and does nothing.
   */
  private reconcileModals(queryParams: Params) {
    // close every modal whose query parameters are gone from the address bar.
    // this happens when the user navigates back, or when another modal took over the same parameters.
    // we go from the newest to the oldest modal, so a modal is never closed on behalf of a modal on top of it
    const openModals = [...this.coreModalService.modals()].reverse();

    openModals.forEach((modal) => {
      if (modal.config.queryParams && !this.webModalService.ownsQueryParameters(queryParams, modal)) {
        this.coreModalService.closeModal(undefined, modal);
      }
    });

    // an address bar without query parameters has nothing to open
    if (!Object.keys(queryParams).length) {
      return;
    }

    // let the modal openers open the modals that belong to them.
    // an opener that reacts to a modal that is already open is handled by the WebModalService
    this.webModalService.newQueryParams.next(queryParams);
  }
}
